import * as d3 from "d3";
import { useMemo } from "react";

import {
  addDays,
  differenceInMinutes,
  endOfDay,
  formatISODateTimeString,
  getDay,
  isAfter,
  isBefore,
  parseFloatTime,
  startOfDay,
  startOfWeek,
} from "@/config/date-fns";
import { EntryFragment } from "@/generated/graphql/graphql";
import { useDimensionStore } from "@/stores/dimensionStore";

import { SquareBlock } from "./SquareBlock";
import { WeeklyBarChartData } from "./WeeklyBarChartData";
import { WeeklyBarChartLayout } from "./WeeklyBarChartLayout";
import { WeeklyBarChartSvg } from "./WeeklyBarChartSvg";
import { WeeklyBarChartTotalData } from "./WeeklyBarChartTotalData";
import { WeeklyBarChartTotalLayout } from "./WeeklyBarChartTotalLayout";

const SQUARE_SIZE = 390;
const MAIN_RATIO = 0.95;
const TOTAL_RATIO = 0.05;

// レイアウト要件:
// - 横幅 100%
// - マージン（上下左右） 40px
// - MAIN(曜日) は内側高さの 4/5、TOTAL(合計) は 1/5（両者の間に 20px のギャップ）
const MARGIN = { bottom: 40, left: 40, right: 40, top: 40 } as const;
const GAP = 60;
// 合計バーを少し短くするための縮小量（ピクセル）
const TOTAL_BAR_SHRINK = 8;

const INNER_WIDTH = SQUARE_SIZE - MARGIN.left - MARGIN.right;
const INNER_HEIGHT = SQUARE_SIZE - MARGIN.top - MARGIN.bottom;

// MAIN/TOTAL の高さは内側領域（上下マージンを除いた領域）からギャップを差し引いて按分
const MAIN_HEIGHT = (INNER_HEIGHT - GAP) * MAIN_RATIO;
const TOTAL_HEIGHT = (INNER_HEIGHT - GAP) * TOTAL_RATIO;

// メインチャートの描画可能高さ（追加マージンは不要）
const MAIN_INNER_HEIGHT = MAIN_HEIGHT;

const WEEKDAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

type WeeklyBarChartProps = {
  baseDate: Date;
  entries: EntryFragment[] | null | undefined;
};

type EntryFragmentWithNowEndAt = Omit<EntryFragment, "endAt"> & {
  endAt: string;
};

type EntrySegment = EntryFragmentWithNowEndAt & {
  endHour: number;
  startHour: number;
};

type EntrySegmentsMap = Map<0 | 1 | 2 | 3 | 4 | 5 | 6, EntrySegment[]>;

export function WeeklyBarChart({ entries, baseDate }: WeeklyBarChartProps) {
  const { dimensionType } = useDimensionStore();

  const datesOfWeek = useMemo<Date[]>(() => {
    const weekStart = startOfWeek(baseDate);
    return Array.from({ length: 7 }, (_, days) => startOfDay(addDays(weekStart, days)));
  }, [baseDate]);

  const entrySegments = useMemo<EntryFragmentWithNowEndAt[]>(() => {
    const allEntries = entries || [];
    return allEntries.map((entry) => {
      const endAt = entry.endAt ? entry.endAt : formatISODateTimeString(new Date());
      return { ...entry, endAt };
    });
  }, [entries]);

  const entrySegmentsMap = useMemo<EntrySegmentsMap>(() => {
    const entrySegmentsMap: EntrySegmentsMap = new Map();

    datesOfWeek.forEach((date) => {
      const startTime = startOfDay(date);
      const endTime = endOfDay(date);
      const day = getDay(startTime) as 0 | 1 | 2 | 3 | 4 | 5 | 6;

      entrySegmentsMap.set(
        day,
        entrySegments
          .filter((entrySegment) => isBefore(entrySegment.startAt, endTime) && isAfter(entrySegment.endAt, startTime))
          .map((entrySegment) => {
            const startAt = isBefore(entrySegment.startAt, startTime)
              ? formatISODateTimeString(startTime)
              : entrySegment.startAt;
            const endAt = isAfter(entrySegment.endAt, endTime) ? formatISODateTimeString(endTime) : entrySegment.endAt;
            const endHour = parseFloatTime(endAt);
            const startHour = parseFloatTime(startAt);
            return { ...entrySegment, endAt, endHour, startAt, startHour };
          }),
      );
    });

    return entrySegmentsMap;
  }, [datesOfWeek, entrySegments]);
  // MAIN スケール
  const x = useMemo(
    () => d3.scaleBand<number>().domain(d3.range(0, 7)).range([0, INNER_WIDTH]).paddingInner(0.2).paddingOuter(0.1),
    [],
  );
  const barWidth = x.bandwidth();
  const y = useMemo(() => d3.scaleLinear().domain([0, 24]).range([MAIN_INNER_HEIGHT, 0]), []);

  // 曜日ごとの集計とキー集合
  const { perDayFromSegments, keys } = useMemo(() => {
    const perDayFromSegments: Array<Map<string, number>> = Array.from({ length: 7 }).map(() => new Map());
    const keysSet = new Set<string>();

    for (
      let dayIndex = 0 as 0 | 1 | 2 | 3 | 4 | 5 | 6;
      dayIndex < 7;
      dayIndex = (dayIndex + 1) as 0 | 1 | 2 | 3 | 4 | 5 | 6
    ) {
      const segments = entrySegmentsMap.get(dayIndex) ?? [];
      segments.forEach((segment) => {
        const hours = Math.max(0, differenceInMinutes(segment.endAt, segment.startAt) / 60);
        if (hours <= 0) return;
        const k = (dimensionType === "feeling" ? segment.feelingKey : segment.categoryKey) as string;
        keysSet.add(k);
        const prev = perDayFromSegments[dayIndex].get(k) ?? 0;
        perDayFromSegments[dayIndex].set(k, prev + hours);
      });
    }

    const keys = Array.from(keysSet.values()).sort();
    return { keys, perDayFromSegments };
  }, [dimensionType, entrySegmentsMap]);

  // 合計用の計算
  const total = useMemo(() => {
    const totalMap = new Map<string, number>();
    perDayFromSegments.forEach((m) => {
      for (const [k, v] of m.entries()) totalMap.set(k, (totalMap.get(k) ?? 0) + v);
    });
    const totalKeys = Array.from(totalMap.keys()).sort();
    const totalUsed = Array.from(totalMap.values()).reduce((a, b) => a + b, 0);
    const leftAlignOffset = x(0) ?? 0;
    const rightEdge = (x(6) ?? 0) + barWidth;
    const totalInnerWidth = rightEdge - leftAlignOffset;
    const totalBarHeight = barWidth;
    const totalBarY = (TOTAL_HEIGHT - totalBarHeight) / 2;
    const totalBarX = d3
      .scaleLinear()
      .domain([0, totalUsed > 0 ? totalUsed : 1])
      .range([0, Math.max(0, totalInnerWidth - TOTAL_BAR_SHRINK)]);

    return { leftAlignOffset, totalBarHeight, totalBarX, totalBarY, totalKeys, totalMap };
  }, [perDayFromSegments, x, barWidth]);

  return (
    <SquareBlock>
      <WeeklyBarChartSvg
        height={SQUARE_SIZE}
        viewBox={`0 0 ${SQUARE_SIZE} ${SQUARE_SIZE}`}
        width="100%"
      >
        {/* MAIN 4/5 */}
        <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
          <WeeklyBarChartLayout
            mainInnerHeight={MAIN_INNER_HEIGHT}
            weekdayLabels={WEEKDAY_LABELS}
            x={x}
            y={y}
          />
          <WeeklyBarChartData
            barWidth={barWidth}
            entrySegmentsMap={entrySegmentsMap}
            keys={keys}
            perDayFromSegments={perDayFromSegments}
            x={x}
            y={y}
          />
        </g>
        {/* TOTAL 1/5 */}
        <g transform={`translate(${MARGIN.left + 14}, ${MARGIN.top + MAIN_HEIGHT + GAP})`}>
          <WeeklyBarChartTotalLayout
            totalBarHeight={total.totalBarHeight}
            totalBarY={total.totalBarY}
          />
          <WeeklyBarChartTotalData
            totalBarHeight={total.totalBarHeight}
            totalBarX={total.totalBarX}
            totalBarY={total.totalBarY}
            totalKeys={total.totalKeys}
            totalMap={total.totalMap}
          />
        </g>
      </WeeklyBarChartSvg>
    </SquareBlock>
  );
}
